// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { NameRec, SeriesKey } from '../types';
import { escapeHtml, formatNumber } from '../utils/format';

/** Names ranked 1..n for a series in a given year, ascending by rank. */
export function topNAt(names: NameRec[], sk: SeriesKey, year: number, n: number): Array<{ name: NameRec; rank: number; count: number }> {
  const out: Array<{ name: NameRec; rank: number; count: number }> = [];
  for (const name of names) {
    for (const p of name.series[sk] || []) {
      if (p[0] === year && p[1] <= n) {
        out.push({ name, rank: p[1], count: p[2] });
        break;
      }
    }
  }
  return out.sort((a, b) => a.rank - b.rank);
}

/** A ranked list (clickable rows open the name drawer via data-key). */
export function rankListHtml(
  rows: Array<{ name: NameRec; rank: number; count: number }>,
  colour: string,
): string {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return `<div class="rank-list">${rows
    .map(
      (r) => `
      <div class="rank-row" data-key="${escapeHtml(r.name.k)}" role="button" tabindex="0" aria-label="${escapeHtml(r.name.n)}, rank ${r.rank}">
        <span class="rank-pos">${r.rank}</span>
        <span class="rank-name">${escapeHtml(r.name.n)}</span>
        <span class="rank-bar-wrap"><span class="rank-bar" style="width:${((r.count / max) * 100).toFixed(1)}%;background:${colour}"></span></span>
        <span class="rank-count">${formatNumber(r.count)}</span>
      </div>`,
    )
    .join('')}</div>`;
}

/** Wire delegated click/keydown on [data-key] rows to open the drawer. */
export function wireNameRows(root: HTMLElement, open: (key: string) => void): void {
  root.addEventListener('click', (e) => {
    const el = (e.target as Element).closest('[data-key]');
    if (el) open(el.getAttribute('data-key') as string);
  });
  root.addEventListener('keydown', (e) => {
    const ke = e as KeyboardEvent;
    if (ke.key !== 'Enter' && ke.key !== ' ') return;
    const el = (e.target as Element).closest('[data-key]');
    if (el) {
      e.preventDefault();
      open(el.getAttribute('data-key') as string);
    }
  });
}

/** Build a segmented control. Returns HTML; caller wires clicks by [data-opt]. */
export function segHtml(name: string, options: Array<{ id: string; label: string }>, current: string): string {
  return `<div class="seg" role="tablist" aria-label="${escapeHtml(name)}">${options
    .map(
      (o) => `<button class="seg-btn" data-opt="${escapeHtml(o.id)}" aria-pressed="${o.id === current}">${escapeHtml(o.label)}</button>`,
    )
    .join('')}</div>`;
}

export function viewHead(title: string, sub: string): string {
  return `<div class="view-head"><h1 class="view-title">${escapeHtml(title)}</h1><p class="view-sub">${sub}</p></div>`;
}
